import Browser         from '../Constants/Browser';
import DeviceType      from '../Constants/DeviceType';
import OperatingSystem from '../Constants/OperatingSystem';
import IUserDevice     from '../Interfaces/IUserDevice';

import UserDevice from './UserDevice';

const cache = {};

const unknownVersion = (-1).toString();
const extractVersion = (match: RegExpExecArray): string => (match ? match[1] : unknownVersion);

const parseBrowserVendor = userAgent => {
    if (/(MSIE|rv:11.*(?=like Gecko))/.test(userAgent)) {
        return Browser.INTERNET_EXPLORER;
    } else if (/like Gecko.*(?=Edge\/)/.test(userAgent)) {
        return Browser.EDGE;
    } else if (/Safari/.test(userAgent) && !/Chrome/.test(userAgent)) {
        return Browser.SAFARI;
    } else if (/Chrome/.test(userAgent) && !/Edge/.test(userAgent) && !/OPR/.test(userAgent)) {
        return Browser.CHROME;
    } else if (/firefox/i.test(userAgent)) {
        return Browser.FIREFOX;
    } else if (/(OPR|Opera)/.test(userAgent)) {
        return Browser.OPERA;
    }

    return Browser.UNKNOWN;
};

const parseBrowserVersion = (browser: Browser, userAgent: string): string => ({
    [Browser.CHROME]: userAgentLocal => extractVersion(/Chrome\/([\d.]+)/g.exec(userAgentLocal)),
    [Browser.SAFARI]: userAgentLocal => extractVersion(/Version\/([\d.]+)/g.exec(userAgentLocal)),
    [Browser.FIREFOX]: userAgentLocal => extractVersion(/Firefox\/([\d.]+)/g.exec(userAgentLocal)),
    [Browser.OPERA]: userAgentLocal => extractVersion(/Version\/([\d.]+)/g.exec(userAgentLocal)),
    [Browser.INTERNET_EXPLORER]: userAgentLocal =>
        extractVersion(/(?:MSIE |Trident\/.*; rv:)([\d.]+)/g.exec(userAgentLocal)),
    [Browser.EDGE]: userAgentLocal => extractVersion(/Edge\/([\d.]+)/g.exec(userAgentLocal)),
    [Browser.UNKNOWN]: (_) => unknownVersion
})[browser](userAgent);

const parseOperatingSystem = (userAgent: string): OperatingSystem => {
    if (/Win/g.test(userAgent)) {
        return OperatingSystem.WINDOWS;
    } else if (/like Mac OS X/g.test(userAgent)) {
        return OperatingSystem.IOS;
    } else if (/Mac/g.test(userAgent)) {
        return OperatingSystem.OSX;
    } else if (/android/gi.test(userAgent)) {
        return OperatingSystem.ANDROID;
    } else if (/Linux/g.test(userAgent)) {
        return OperatingSystem.LINUX;
    }

    return OperatingSystem.UNKNOWN;
};

const parseOperatingSystemVersion = (operatingSystem: OperatingSystem, userAgent: string): string => ({
    [OperatingSystem.ANDROID]: userAgentLocal => extractVersion(/android ([\d\.]+);/gi.exec(userAgentLocal)),
    [OperatingSystem.IOS]: userAgentLocal =>
        extractVersion(/ OS ([\d_]+)/g.exec(userAgentLocal)).replace(/_/g, '.'),
    [OperatingSystem.OSX]: userAgentLocal =>
        extractVersion(/Mac OS X ([\d_]+)/g.exec(userAgentLocal)).replace(/_/g, '.'),
    [OperatingSystem.WINDOWS]: userAgentLocal => extractVersion(/Windows ([\w\. ]+);/g.exec(userAgentLocal)),
    [OperatingSystem.LINUX]: (_) => unknownVersion,
    [OperatingSystem.UNKNOWN]: (_) => unknownVersion
})[operatingSystem](userAgent);

const parseDeviceType = (operatingSystem: OperatingSystem, _: string): DeviceType => {
    switch (operatingSystem) {
        case OperatingSystem.IOS:
        case OperatingSystem.ANDROID:
            return DeviceType.MOBILE;
        default:
            return DeviceType.DESKTOP;
    }
};

const getUserDevice = (userAgent: string): UserDevice => {
    if (cache[userAgent] instanceof UserDevice) return cache[userAgent];

    const browser = parseBrowserVendor(userAgent);
    const operatingSystem = parseOperatingSystem(userAgent);

    const userDeviceData: IUserDevice = {
        browser,
        browserVersion: parseBrowserVersion(browser, userAgent),
        os: operatingSystem,
        osVersion: parseOperatingSystemVersion(operatingSystem, userAgent),
        deviceType: parseDeviceType(operatingSystem, userAgent)
    };

    return (cache[userAgent] = Object.assign(new UserDevice(), userDeviceData));
};

export default getUserDevice;
